// Creative asset ROLES — client-side mirror of the backend authority
// (backend/services/creativeStorageArtifacts.js).
//
// This file is intentionally BYTE-IDENTICAL between the client and superadmin
// apps. Both Creative Studio surfaces must decide "is this a client source
// creative?" the same way, or the two apps show different inventories for the
// same advertiser.
//
// Cphere generates internal artifacts from a client's uploads — preview
// thumbnails, poster frames, frozen snapshots, exports. They are storage
// implementation details. They must never be presented as creatives: not as a
// title, a review filename, an asset label, a download filename, a search
// result, a filter value, or an accessibility label, and never counted in the
// Creative Library.
//
// The backend already withholds them. This module is the second, independent
// guard on the surface itself, so a regression on either side of the wire
// cannot put an internal artifact in front of a client.
//
// Two signals, checked in this order:
//   1. The explicit persisted role on the record (`asset_role` / `assetRole`).
//   2. The reserved Cphere storage namespaces in the filename, for records
//      that predate the explicit role.
// Anything not provably SOURCE is withheld.

export const CREATIVE_ASSET_ROLES = Object.freeze({
  SOURCE: "SOURCE",
  PREVIEW_DERIVATIVE: "PREVIEW_DERIVATIVE",
  THUMBNAIL: "THUMBNAIL",
  EXPORT: "EXPORT",
});

export const RESERVED_CREATIVE_ARTIFACT_PREFIXES = Object.freeze([
  "cphere-derived-",
  "cphere-frozen-",
]);

const DERIVED_ROLES = Object.freeze([
  CREATIVE_ASSET_ROLES.PREVIEW_DERIVATIVE,
  CREATIVE_ASSET_ROLES.THUMBNAIL,
  CREATIVE_ASSET_ROLES.EXPORT,
]);

function basenameOf(filename) {
  const text = String(filename || "").trim();
  if (!text) return "";
  const parts = text.split("/");
  return parts[parts.length - 1];
}

function normalizeRole(value) {
  const normalized = String(value || "").trim().toUpperCase();
  return CREATIVE_ASSET_ROLES[normalized] || null;
}

// True when the basename sits in a reserved Cphere storage namespace.
// Case-insensitive prefix match on the basename only, so ordinary names that
// merely CONTAIN "derived" or "frozen" are unaffected.
export function isReservedCphereArtifactName(filename) {
  const basename = basenameOf(filename).toLowerCase();
  if (!basename) return false;
  return RESERVED_CREATIVE_ARTIFACT_PREFIXES.some((prefix) => basename.startsWith(prefix));
}

export function resolveCreativeAssetRole(asset) {
  const filename =
    asset?.file_name || asset?.fileName || asset?.filename || asset?.name || "";
  const explicit = normalizeRole(asset?.asset_role) || normalizeRole(asset?.assetRole);
  const reservedName = isReservedCphereArtifactName(filename);

  if (explicit && DERIVED_ROLES.includes(explicit)) return explicit;
  // A reserved name is never overridden by a SOURCE claim: that combination
  // means something mislabelled a generated object, so we fail closed.
  if (reservedName) return CREATIVE_ASSET_ROLES.PREVIEW_DERIVATIVE;
  if (explicit) return explicit;
  return CREATIVE_ASSET_ROLES.SOURCE;
}

export function isClientVisibleCreativeAsset(asset) {
  return resolveCreativeAssetRole(asset) === CREATIVE_ASSET_ROLES.SOURCE;
}

// The single funnel every creative list passes through before it reaches
// state. Applying it once at ingestion means counts, search, filters, labels
// and downloads all inherit the guarantee instead of re-implementing it.
export function filterClientVisibleCreativeAssets(assets) {
  if (!Array.isArray(assets)) return [];
  return assets.filter((asset) => isClientVisibleCreativeAsset(asset));
}
